import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";
import { Save } from "lucide-react";

const SECTIONS = [
  {
    title: "Branding",
    fields: [
      { k: "site_name", label: "Site name" },
      { k: "tagline", label: "Tagline" },
      { k: "logo_url", label: "Logo URL (recommended square JPG/PNG)" },
      { k: "favicon_url", label: "Favicon URL" },
      { k: "hero_title", label: "Homepage hero title", textarea: true },
      { k: "hero_subtitle", label: "Homepage hero subtitle", textarea: true },
      { k: "footer_about", label: "Footer about text", textarea: true },
    ],
  },
  {
    title: "Business details",
    fields: [
      { k: "academy_name", label: "Academy / business name" },
      { k: "email", label: "Primary email" },
      { k: "support_email", label: "Support email" },
      { k: "phone_primary", label: "Primary phone" },
      { k: "phone_secondary", label: "Secondary phone" },
      { k: "whatsapp", label: "WhatsApp number (with country code e.g. +919876543210)" },
      { k: "business_hours", label: "Business hours" },
    ],
  },
  {
    title: "Address",
    fields: [
      { k: "address_line1", label: "Address line 1" },
      { k: "address_line2", label: "Address line 2" },
      { k: "city", label: "City" },
      { k: "state", label: "State" },
      { k: "pincode", label: "PIN code" },
      { k: "country", label: "Country" },
      { k: "google_maps_url", label: "Google Maps URL" },
    ],
  },
  {
    title: "Social media",
    fields: [
      { k: "instagram_url", label: "Instagram URL" },
      { k: "facebook_url", label: "Facebook URL" },
      { k: "youtube_url", label: "YouTube URL" },
      { k: "twitter_url", label: "Twitter / X URL" },
      { k: "linkedin_url", label: "LinkedIn URL" },
    ],
  },
];

export default function SettingsTab() {
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(settings); }, [settings]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      // Only send fields present
      const payload = {};
      for (const sec of SECTIONS) {
        for (const f of sec.fields) payload[f.k] = form[f.k] ?? "";
      }
      await api.put("/settings", payload);
      await refresh();
      toast.success("Settings saved. Changes are live across the website.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          These fields appear in the header, footer, Contact page and anywhere contact details are shown.
        </p>
        <Button onClick={save} disabled={saving} className="bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid="settings-save-btn">
          <Save className="mr-2 h-4 w-4" /> {saving ? "Saving..." : "Save all changes"}
        </Button>
      </div>
      {SECTIONS.map((sec) => (
        <Card key={sec.title} className="border-border/70">
          <CardContent className="p-6">
            <div className="font-display text-lg font-bold">{sec.title}</div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {sec.fields.map((f) => (
                <div key={f.k} className={f.textarea ? "sm:col-span-2" : ""}>
                  <Label htmlFor={f.k}>{f.label}</Label>
                  {f.textarea ? (
                    <Textarea id={f.k} rows={2} value={form[f.k] || ""} onChange={set(f.k)} data-testid={`settings-${f.k}`} className="mt-1.5" />
                  ) : (
                    <Input id={f.k} value={form[f.k] || ""} onChange={set(f.k)} data-testid={`settings-${f.k}`} className="mt-1.5" />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid="settings-save-btn-2">
          <Save className="mr-2 h-4 w-4" /> {saving ? "Saving..." : "Save all changes"}
        </Button>
      </div>
    </div>
  );
}
